import { Texture } from 'three';

import type { ThreeSceneEngine } from '../three-scene-engine.class';
import type { SceneBuildManifest } from '../../types/scene-build-manifest.type';
import { KitchenSceneBuilder } from '../../builders';
import { TextureManager } from '../../managers/texture-manager/texture-manager.class';

export type VerifySceneManifestResult = {
	ready: boolean;
	missing: Array<string>;
};

export const verifySceneManifestCfn = (
	self: ThreeSceneEngine,
	manifest: SceneBuildManifest
): VerifySceneManifestResult => {
	const missing: Array<string> = [];

	// Zero-appliance manifests are always ready on the appliance axis
	if (manifest.appliances.length > 0) {
		const objectHolder = self.sceneManager?.objectHolder;
		if (!objectHolder) {
			missing.push('scene objectHolder not available');
		} else {
			for (const expected of manifest.appliances) {
				let found = false;
				objectHolder.traverse((child) => {
					if (!found && child.userData?.type === 'appliance' && child.userData?.id === expected.id) {
						found = true;
					}
				});
				if (!found) {
					missing.push(`appliance id="${expected.id}" sku="${expected.sku}" island=${expected.islandIndex}`);
				}
			}
		}
	}

	// Countertop check: only islands whose manifest entry carries a key expect a countertop mesh
	const expectedCountertops = manifest.countertops.filter((c) => c.key !== null && c.key !== 'none');
	if (expectedCountertops.length > 0) {
		const objectHolder = self.sceneManager?.objectHolder;
		if (objectHolder) {
			for (const { islandIndex: i } of expectedCountertops) {
				let hasCountertop = false;
				objectHolder.traverse((child) => {
					if (hasCountertop) return;
					const { type, islandIndex, id } = (child.userData ?? {}) as { type?: string; islandIndex?: number; id?: string };
					// CSG cutout replacement meshes keep the island-N-countertop id but can drop islandIndex.
					if ((type === 'countertop' || type === 'countertop-no-cutout') && (islandIndex === i || (typeof id === 'string' && id.includes(`island-${i}-`)))) {
						hasCountertop = true;
					}
				});
				if (!hasCountertop) {
					missing.push(`countertop for island=${i}`);
				}
			}
		}
	}

	// Texture completeness: a texture object attached to a scene material whose image has not
	// arrived yet would make GLTFExporter throw 'No valid image data' — treat as not-ready.
	{
		const objectHolder = self.sceneManager?.objectHolder;
		if (objectHolder) {
			let pendingTextures = 0;
			objectHolder.traverse((child) => {
				const mesh = child as { isMesh?: boolean; material?: unknown };
				if (!mesh.isMesh || !mesh.material) return;
				const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
				for (const mat of materials as Array<Record<string, { image?: { width?: number } } | null>>) {
					for (const slot of ['map', 'normalMap', 'alphaMap', 'emissiveMap', 'roughnessMap', 'metalnessMap'] as const) {
						const texture = mat?.[slot];
						if (texture && !texture.image) pendingTextures++;
					}
				}
			});
			if (pendingTextures > 0) missing.push(`${pendingTextures} texture image(s) still loading`);
		}
	}

	// Corner-trim check: a trim mesh with no map is the white-default fallback the pending-image scan can't see; fail so the existing self-heal recomposes.
	if (manifest.trimKey) {
		const objectHolder = self.sceneManager?.objectHolder;
		if (objectHolder) {
			let untexturedTrims = 0;
			objectHolder.traverse((child) => {
				const mesh = child as { isMesh?: boolean; userData?: { type?: string }; material?: { map?: unknown } | Array<{ map?: unknown }> };
				if (!mesh.isMesh || mesh.userData?.type !== 'corner') return;
				const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
				if (material && !material.map) untexturedTrims++;
			});
			if (untexturedTrims > 0) missing.push(`${untexturedTrims} corner trim(s) missing texture (key="${manifest.trimKey}")`);
		}
	}

	// Sky check: when a non-background sky key is expected, scene.background must be a Texture
	const skyKey = manifest.skyKey;
	if (skyKey && skyKey !== 'background') {
		const scene = self.sceneManager?.scene;
		if (scene && !(scene.background instanceof Texture)) {
			missing.push(`sky texture not applied (key="${skyKey}")`);
		}
	}

	// Surface permanently-failed manifest-relevant texture keys so readiness fails with a named cause instead of spinning until timeout
	const textureManager = self.textureManager;
	if (textureManager instanceof TextureManager) {
		const failedKeys = textureManager.failedKeysAccess;
		if (failedKeys.size > 0) {
			const relevantKeys = [
				manifest.surroundKey,
				manifest.trimKey,
				...manifest.countertops.map((c) => c.key),
				manifest.skyKey,
				manifest.floorKey
			].filter((k): k is string => k !== null && k !== undefined);
			for (const key of relevantKeys) {
				if (failedKeys.has(key)) {
					missing.push(`texture permanently failed (key="${key}")`);
				}
			}
		}
	}

	return { ready: missing.length === 0, missing };
};
